/*
 * SundayBuilder™ · The Intelligent Planning Desk (Concept D)
 * ---------------------------------------------------------------------
 * Orchestrates five screens:
 *   1 · Proposition  · GET /api/sundaybuilder/next-sunday
 *   2 · Context      · one optional textarea
 *   3 · Discernment  · POST /api/sundaybuilder/generate + poll
 *   4 · Dossier      · GET /api/parish/job/:jobId
 *   5 · Hand-off     · Download PDF/DOCX
 *
 * Backend-authoritative — the frontend never fabricates dates,
 * occasions, seasons, readings, or draft state.
 * =================================================================== */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { track } from "../lib/track";
import { useAuth } from "../auth/AuthContext";
import Proposition from "./sundaybuilder/Proposition";
import ContextNotes from "./sundaybuilder/ContextNotes";
import Discernment from "./sundaybuilder/Discernment";
import DossierFolio from "./sundaybuilder/DossierFolio";
import HandOff from "./sundaybuilder/HandOff";
import "./sundaybuilder/workspace.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Screen 1's altar-frontal band only. Retrieved at index-loader
// initialization so CRA doesn't try to inline it. Falls back to a
// solid stone if the asset is missing.
const ALTAR_URL = `${process.env.PUBLIC_URL || ""}/brand/altar-frontal.webp`;

export default function WorshipPlanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const subscribed = ["parish_pro", "cathedral", "diocese"].includes(user?.entitlements?.plan)
                     && user?.entitlements?.plan_status === "active";

  // Screens: proposition → context → discernment → dossier → handoff
  const [screen, setScreen]         = useState("proposition");
  const [envelope, setEnvelope]     = useState(null);
  const [envError, setEnvError]     = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);   // ISO date the user is preparing
  const [selectedSunday, setSelectedSunday] = useState(null); // full Sunday object for context labels
  const [notes, setNotes]           = useState("");
  const [jobId, setJobId]           = useState(null);
  const [plan, setPlan]             = useState(null);
  const [explain, setExplain]       = useState(null);
  const [memoryPreview, setMemoryPreview] = useState(null);
  const [error, setError]           = useState(null);
  const [upgrade, setUpgrade]       = useState(null);
  const [busy, setBusy]             = useState("");

  /* ---- Load the Planning Desk envelope on mount ---- */
  useEffect(() => {
    axios.get(`${API}/sundaybuilder/next-sunday`)
      .then((r) => {
        setEnvelope(r.data);
        if (r.data?.next_sunday) {
          setSelectedDate(r.data.next_sunday.iso);
          setSelectedSunday(r.data.next_sunday);
        }
      })
      .catch((e) => {
        setEnvError(e.response?.data?.detail || "Planning Desk is temporarily unavailable.");
        setEnvelope({ state: "unavailable", today: { iso: "", label: "", season: "" } });
      });
  }, []);

  /* ---- Screen 1 handlers ---- */
  const handlePrepare = () => {
    // Go straight to context screen with the resolved Sunday.
    setScreen("context");
  };

  const handleContinueDraft = async () => {
    // If the envelope reports a draft_job_id, resume by polling it.
    const draftId = envelope?.next_sunday?.draft_job_id;
    if (draftId) {
      setJobId(draftId);
      // Poll the existing job.
      try {
        const r = await axios.get(`${API}/parish/job/${draftId}`);
        if (r.data.status === "done") {
          setPlan(r.data.report);
          setExplain(r.data.explain || null);
          setMemoryPreview(r.data.memory_preview || null);
          setScreen("dossier");
          return;
        }
      } catch (_) {}
    }
    // Otherwise treat like a fresh prepare using the resolved date.
    handlePrepare();
  };

  const handleStartOver = async () => {
    // Discard the current draft: leave it in the DB (audit) but
    // proceed as a fresh prepare. The next generate call will
    // produce a new job_id.
    setPlan(null); setExplain(null); setMemoryPreview(null); setJobId(null);
    handlePrepare();
  };

  const handleManualDate = (iso) => {
    setSelectedDate(iso);
    // Build a minimal Sunday shape so the context screen labels
    // reflect the user's choice. No occasion inference client-side —
    // backend will infer during /generate.
    setSelectedSunday({ iso, label: iso, occasion: "" });
    setScreen("context");
  };

  /* ---- Screen 2 → 3 · start generation ---- */
  const startGeneration = async (userNotes) => {
    setError(null); setUpgrade(null); setPlan(null); setExplain(null);
    setMemoryPreview(null); setJobId(null); setNotes(userNotes || "");
    setScreen("discernment");
    try {
      const start = await axios.post(`${API}/sundaybuilder/generate`, {
        date: selectedDate,
        notes: userNotes || "",
      });
      const jid = start.data.job_id;
      setJobId(jid);
      track("SundayBuilder Started", { scope: "Single Sunday", date: selectedDate });
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API}/parish/job/${jid}`);
          if (r.data.status === "done") {
            setPlan(r.data.report);
            setExplain(r.data.explain || null);
            setMemoryPreview(r.data.memory_preview || null);
            setScreen("dossier");
          } else if (r.data.status === "error") {
            setError({ kind: "generation_failed", message: r.data.detail || "The dossier could not be prepared." });
            setScreen("context");
          } else if (attempts > 60) {
            setError({ kind: "temporary", message: "Preparation is taking longer than expected. Please try again." });
            setScreen("context");
          } else {
            setTimeout(poll, 3000);
          }
        } catch {
          if (attempts > 60) {
            setError({ kind: "temporary", message: "We lost contact with the archive. Please try again." });
            setScreen("context");
          } else {
            setTimeout(poll, 3000);
          }
        }
      };
      setTimeout(poll, 2500);
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (status === 402 && d && typeof d === "object") {
        setUpgrade(d.message);
        setScreen("context");
      } else if (status === 400 && (d?.code === "profile_incomplete" || (typeof d === "string" && d.includes("Parish Profile")))) {
        setError({ kind: "profile", message: d?.message || d || "Please complete your Parish Profile before preparing worship." });
        setScreen("context");
      } else if (status === 503) {
        setError({ kind: "configuration", message: typeof d === "string" ? d : "The archive is being tended to. Preparation will resume shortly." });
        setScreen("context");
      } else if (status >= 500) {
        setError({ kind: "temporary", message: "A momentary interruption. Please try again." });
        setScreen("context");
      } else {
        setError({ kind: "temporary", message: (typeof d === "string" && d) || "Could not begin preparation. Please try again." });
        setScreen("context");
      }
    }
  };

  /* ---- Screen 4 → 5 handlers ---- */
  const handleRefine = () => setScreen("context");
  const handleContinueExport = () => setScreen("handoff");
  const handleNewPlan = () => {
    setScreen("proposition"); setSelectedDate(envelope?.next_sunday?.iso || null);
    setSelectedSunday(envelope?.next_sunday || null); setNotes("");
    setPlan(null); setExplain(null); setMemoryPreview(null); setJobId(null); setError(null); setUpgrade(null);
  };

  const startCheckout = async (planKey) => {
    track("Upgrade Button Clicked", { plan: planKey, from: "worship_planner" });
    setBusy(planKey);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { plan: planKey, origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError({ kind: "temporary", message: e.response?.data?.detail || "Could not start checkout. Please try again." });
      setBusy("");
    }
  };

  /* ---- Set the altar-frontal CSS var at mount ---- */
  useEffect(() => {
    document.documentElement.style.setProperty("--li-altar-frontal-url", `url("${ALTAR_URL}")`);
  }, []);

  /* ---- Render ---- */
  return (
    <div className="sb-workspace" data-testid="worship-planner-page">
      <div className="sb-topbar" data-testid="wp-topbar">
        <div className="sb-brand">Sunday<em>Builder</em>™</div>
        <button className="sb-back" onClick={() => navigate("/dashboard")} data-testid="wp-back">
          ← Dashboard
        </button>
      </div>

      {/* Altar frontal band — Screen 1 only */}
      {screen === "proposition" && (
        <div className="sb-altar-hero" role="img" aria-label="Bas-relief altar frontal">
          <div className="sb-altar-hero__body">
            <div className="sb-altar-hero__brand">
              <img
                className="sb-altar-hero__monogram"
                src={`${process.env.PUBLIC_URL || ""}/brand/li-monogram.png`}
                alt=""
                aria-hidden="true"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <span className="sb-altar-hero__title">SundayBuilder™</span>
            </div>
            <span className="sb-altar-hero__wordmark">Planning Desk</span>
          </div>
        </div>
      )}

      {screen === "proposition" && (
        <div className="sb-screen">
          {envelope?.today?.label && (
            <p className="sb-today" data-testid="sb-today-line">
              {envelope.today.label}
              {envelope.today.season ? ` · ${envelope.today.season}` : ""}
            </p>
          )}
          {envelope?.state !== "profile_incomplete" && envelope?.state !== "unavailable" && (
            <h1 className="sb-h1" data-testid="sb-proposition-heading-main">Your next Sunday is</h1>
          )}

          {upgrade && (
            <div style={{
              marginBottom: 24, padding: 20,
              background: "var(--li-card-bg)", border: "1px solid var(--li-border)",
              borderTop: "3px solid var(--li-cast-bronze)", borderRadius: 6,
            }} data-testid="wp-upgrade">
              <p style={{ margin: 0, marginBottom: 16, fontFamily: "var(--li-font-body)", fontSize: "1rem" }}>
                {upgrade}
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button className="sb-btn ghost" disabled={busy} onClick={() => startCheckout("single_report")} data-testid="wp-buy-single">
                  {busy === "single_report" ? "Redirecting…" : "Purchase Single Report ($29)"}
                </button>
                <button className="sb-btn primary" disabled={busy} onClick={() => startCheckout("parish_pro")} data-testid="wp-upsell-btn">
                  {busy === "parish_pro" ? "Redirecting…" : "Upgrade to Parish Pro ($49/mo)"}
                </button>
              </div>
            </div>
          )}

          <Proposition
            envelope={envelope}
            onPrepare={handlePrepare}
            onContinueDraft={handleContinueDraft}
            onStartOverConfirmed={handleStartOver}
            onManualDate={handleManualDate}
          />
        </div>
      )}

      {screen === "context" && (
        <>
          {error && (
            <div className="sb-screen" style={{ marginBottom: 0 }}>
              <div style={{
                padding: 20, background: "var(--li-card-bg)",
                border: "1px solid var(--li-border)",
                borderTop: "3px solid var(--li-cardinal-burgundy)",
                borderRadius: 6, marginBottom: 24,
              }} data-testid="wp-gen-error">
                <p style={{ margin: 0, marginBottom: 12, fontFamily: "var(--li-font-body)", fontWeight: 600, fontSize: "1.0625rem" }}
                   data-testid="wp-gen-error-title">
                  {error.kind === "generation_failed" ? "Dossier preparation failed"
                    : error.kind === "profile" ? "Finish your Parish Profile"
                    : error.kind === "configuration" ? "Preparation is not currently available"
                    : "Temporary interruption"}
                </p>
                <p style={{ margin: 0, marginBottom: 12, fontFamily: "var(--li-font-body)", fontSize: "1rem" }}
                   data-testid="wp-gen-error-message">{error.message}</p>
                <div style={{ display: "flex", gap: 10 }}>
                  {error.kind === "profile" ? (
                    <button className="sb-btn primary" onClick={() => navigate("/parish-profile")} data-testid="wp-gen-error-profile">
                      Complete Parish Profile
                    </button>
                  ) : (
                    <button className="sb-btn ghost" onClick={() => setError(null)} data-testid="wp-gen-error-dismiss">
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          <ContextNotes
            sunday={selectedSunday}
            initialNotes={notes}
            onBack={() => { setError(null); setScreen("proposition"); }}
            onPrepare={startGeneration}
          />
        </>
      )}

      {screen === "discernment" && <Discernment />}

      {screen === "dossier" && plan && (
        <DossierFolio
          plan={plan}
          jobId={jobId}
          explain={explain}
          memoryPreview={memoryPreview}
          subscribed={subscribed}
          sundayLabel={selectedSunday ? `${selectedSunday.label}${selectedSunday.occasion ? ` · ${selectedSunday.occasion}` : ""}` : ""}
          sundayDate={selectedDate}
          onRefine={handleRefine}
          onContinueExport={handleContinueExport}
          onPlanUpdate={setPlan}
        />
      )}

      {screen === "handoff" && plan && (
        <HandOff plan={plan} onNewPlan={handleNewPlan} />
      )}
    </div>
  );
}
